import { isAfter } from "@sembark-travel/datetime-utils"
import { TFormattedMoney } from "@sembark-travel/money"
import { validateFormValues } from "@sembark-travel/ui-form"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import * as Validator from "yup"
import { IDocGroup } from "../DocGroups"
import { ICountry } from "../Locations"
import { IUser } from "../Users"
import { TWorldCountry } from "../World"
import pluralize from "pluralize"

export type TVisa = {
	id?: number
	name?: string
	currency?: string
	countries?: TWorldCountry[]
	trip_destinations?: ICountry[]
	document_groups?: IDocGroup[]
	visa_types: TVisaType[]
	created_at?: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
}

export type TVisaType = {
	id?: number
	name?: string
	type_of_visa?: string
	processing_time_in_hours?: string
	duration?: string
	duration_iso?: string
	duration_formatted?: string
	entry_type?: string
	purpose?: string
	cost_price?: TFormattedMoney
	selling_price?: TFormattedMoney
	price_valid_till?: string
	apply_before_days?: string
	document_groups?: IDocGroup[]
	description?: string
	created_at?: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
}

export type TVisaServiceForm = {
	id?: number
	name?: string
	currency?: string
	countries?: TWorldCountry[]
	destinations?: ICountry[]
	document_groups?: IDocGroup
	visa_types?: TVisaTypeForm[]
	created_at?: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
}

export type TVisaTypeForm = {
	id?: number
	name?: string
	type_of_visa?: { name: string; value: string }
	entry_type?: { name: string; value: string }
	purpose?: { name: string; value: string }
	day_and_hour?: { days: number; hours: number }
	duration_formatted?: string
	duration?: string
	duration_iso?: string
	cost_price?: string
	selling_price?: string
	price_valid_till?: string
	apply_before_days?: string
	document_groups?: IDocGroup
	description?: string
	created_at?: string
	created_by?: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
}

type TVisaPostPayload = {
	name: string
	trip_destinations: number[] | null
	currency: string
	countries: string[]
	document_groups:
		| {
				name: string
				document_type_ids: number[]
		  }[]
		| null
	visa_types: TVisaTypeUpdatePayload[]
}

type TVisaTypeUpdatePayload = {
	name: string
	type_of_visa: string
	purpose: string
	entry_type: string
	processing_time_in_hours: number | null
	duration: string | null
	cost_price: number
	selling_price: number
	price_valid_till: string
	apply_before_days: number | null
	description: string | null
	document_groups:
		| {
				name: string
				document_type_ids: number[]
		  }[]
		| null
}

export function XHR(xhr: XHRInstance) {
	return {
		async createVisaService(data: TVisaServiceForm) {
			const formattedData: TVisaPostPayload = {
				name: data.name ?? "",
				currency: data.currency ?? "",
				trip_destinations: data.destinations
					? data.destinations.map((d) => d.id)
					: null,
				countries: data.countries ? data.countries.map((c) => c.iso2) : [],
				document_groups: data.document_groups
					? [
							{
								name: data.document_groups.name,
								document_type_ids: Array.isArray(
									data.document_groups.document_types
								)
									? data.document_groups.document_types.map((dt) => dt.id)
									: [],
							},
						]
					: null,
				visa_types: (Array.isArray(data.visa_types) ? data.visa_types : []).map(
					(vt) => ({
						name: vt.name ?? "",
						type_of_visa: vt.type_of_visa?.value ?? "",
						purpose: vt.purpose?.value ?? "",
						entry_type: vt.entry_type?.value ?? "",
						processing_time_in_hours: vt.day_and_hour
							? vt.day_and_hour.days * 24 + vt.day_and_hour.hours
							: null,
						duration: vt.duration ? vt.duration : null,
						cost_price:
							vt.cost_price !== undefined &&
							vt.cost_price !== null &&
							vt.cost_price !== ""
								? Number(vt.cost_price)
								: 0,
						selling_price:
							vt.selling_price !== undefined &&
							vt.selling_price !== null &&
							vt.selling_price !== ""
								? Number(vt.selling_price)
								: 0,
						price_valid_till: vt.price_valid_till ?? "",
						apply_before_days:
							vt.apply_before_days !== undefined &&
							vt.apply_before_days !== null &&
							vt.apply_before_days !== ""
								? Number(vt.apply_before_days)
								: null,
						description: vt.description ?? null,
						document_groups: vt.document_groups
							? [
									{
										name: vt.document_groups.name,
										document_type_ids: Array.isArray(
											vt.document_groups.document_types
										)
											? vt.document_groups.document_types.map((dt) => dt.id)
											: [],
									},
								]
							: null,
					})
				),
			}
			return xhr.post<TVisa>("/visa-services", formattedData)
		},
		async updateVisaService(visaId: string, data: TVisaServiceForm) {
			const formattedData: TVisaPostPayload = {
				name: data.name ?? "",
				currency: data.currency ?? "",
				trip_destinations: data.destinations
					? data.destinations.map((d) => d.id)
					: null,
				countries: data.countries
					? data.countries
							.map((c) => c.iso2)
							.filter(
								(country): country is string =>
									typeof country === "string" && country.length > 0
							)
					: [],

				document_groups: data.document_groups
					? [
							{
								name: data.document_groups.name,
								document_type_ids: Array.isArray(
									data.document_groups.document_types
								)
									? data.document_groups.document_types.map((dt) => dt.id)
									: [],
							},
						]
					: null,
				visa_types: (Array.isArray(data.visa_types) ? data.visa_types : []).map(
					(vt) => ({
						name: vt.name ?? "",
						type_of_visa: vt.type_of_visa?.value ?? "",
						purpose: vt.purpose?.value ?? "",
						entry_type: vt.entry_type?.value ?? "",
						processing_time_in_hours: vt.day_and_hour
							? vt.day_and_hour.days * 24 + vt.day_and_hour.hours
							: null,
						duration: vt.duration ? vt.duration : null,
						cost_price:
							vt.cost_price !== undefined &&
							vt.cost_price !== null &&
							vt.cost_price !== ""
								? Number(vt.cost_price)
								: 0,
						selling_price:
							vt.selling_price !== undefined &&
							vt.selling_price !== null &&
							vt.selling_price !== ""
								? Number(vt.selling_price)
								: 0,
						price_valid_till: vt.price_valid_till ?? "",
						apply_before_days:
							vt.apply_before_days !== undefined &&
							vt.apply_before_days !== null &&
							vt.apply_before_days !== ""
								? Number(vt.apply_before_days)
								: null,
						description: vt.description ?? null,
						document_groups: vt.document_groups
							? [
									{
										name: vt.document_groups.name,
										document_type_ids: Array.isArray(
											vt.document_groups.document_types
										)
											? vt.document_groups.document_types.map((dt) => dt.id)
											: [],
									},
								]
							: null,
					})
				),
			}
			return xhr.put<{ data: TVisa }>(`/visa-services/${visaId}`, formattedData)
		},
		async showVisaServices(
			visaId?: number | string,
			params?: unknown
		): Promise<TVisa> {
			return xhr
				.get(`/visa-services/${visaId}`, { params })
				.then(({ data }) => data.data)
		},
		async getVisaServices(params: unknown): Promise<IListResponse<TVisa>> {
			return xhr.get(`/visa-services`, { params }).then(({ data }) => data)
		},
		async getVisaServicesForSelect(params: unknown): Promise<TVisa[]> {
			return xhr.get(`/visa-services`, { params }).then(({ data }) => data.data)
		},
		async updateVisaType(visaTypeId: number, data: TVisaTypeForm) {
			const formattedData: TVisaTypeUpdatePayload = {
				name: data.name ?? "",
				type_of_visa: data.type_of_visa?.value ?? "",
				purpose: data.purpose?.value ?? "",
				entry_type: data.entry_type?.value ?? "",
				processing_time_in_hours: data.day_and_hour
					? data.day_and_hour.days * 24 + data.day_and_hour.hours
					: null,
				duration: data.duration ? data.duration : null,
				cost_price:
					data.cost_price !== undefined &&
					data.cost_price !== null &&
					data.cost_price !== ""
						? Number(data.cost_price)
						: 0,
				selling_price:
					data.selling_price !== undefined &&
					data.selling_price !== null &&
					data.selling_price !== ""
						? Number(data.selling_price)
						: 0,
				price_valid_till: data.price_valid_till ?? "",
				apply_before_days:
					data.apply_before_days !== undefined &&
					data.apply_before_days !== null &&
					data.apply_before_days !== ""
						? Number(data.apply_before_days)
						: null,
				description: data.description ?? null,
				document_groups: data.document_groups
					? [
							{
								name: data.document_groups.name,
								document_type_ids: Array.isArray(
									data.document_groups.document_types
								)
									? data.document_groups.document_types.map((dt) => dt.id)
									: [],
							},
						]
					: null,
			}
			return xhr.put<{ data: TVisaType }>(
				`/visa-types/${visaTypeId}`,
				formattedData
			)
		},
		async disableVisaService(serviceId: number): Promise<{ message: string }> {
			return xhr
				.delete(`/visa-services/${serviceId}`)
				.then(({ data }) => data.data)
		},
		async disableVisaType(typeId: number): Promise<{ message: string }> {
			return xhr.delete(`/visa-types/${typeId}`).then(({ data }) => data.data)
		},
		async restoreVisaType(typeId: number): Promise<{ message: string }> {
			return xhr.patch(`/visa-types/${typeId}/restore`).then(({ data }) => data)
		},
	}
}

export const TYPE_OF_VISA_OPTIONS = [
	{ name: "E-Visa", value: "e-visa" },
	{ name: "Schengen Visa", value: "schengen_visa" },
	{ name: "On-Arrival Visa", value: "on-arrival_visa" },
	{ name: "Transit Visa", value: "transit_visa" },
	{ name: "Sticker Visa", value: "sticker_visa" },
]

export const ENTRY_TYPE_OPTIONS = [
	{ name: "Single", value: "single" },
	{ name: "Multiple", value: "multiple" },
	{ name: "Double", value: "double" },
]

export const PURPOSE_OF_VISA_OPTIONS = [
	{ name: "Business", value: "business" },
	{ name: "Tourism", value: "tourism" },
	{ name: "Medical", value: "medical" },
	{ name: "Student", value: "student" },
	{ name: "Work/Employment", value: "work_employment" },
	{ name: "Transit", value: "transit" },
	{ name: "Journalist", value: "journalist" },
]

export function getTypeOfVisaDisplayName(type?: string): string {
	return (
		TYPE_OF_VISA_OPTIONS.find((option) => option.value === type)?.name ?? ""
	)
}

export function getEntryTypeDisplayName(type?: string): string {
	return ENTRY_TYPE_OPTIONS.find((option) => option.value === type)?.name ?? ""
}

export function getPurposeOfVisaDisplayName(type?: string): string {
	return (
		PURPOSE_OF_VISA_OPTIONS.find((option) => option.value === type)?.name ?? ""
	)
}

export function getTypeOfVisaOption(type?: string) {
	return (
		TYPE_OF_VISA_OPTIONS.find((option) => option.value === type) ?? undefined
	)
}

export function getEntryTypeOption(type?: string) {
	return ENTRY_TYPE_OPTIONS.find((option) => option.value === type) ?? undefined
}

export function getPurposeOfVisaOption(type?: string) {
	return (
		PURPOSE_OF_VISA_OPTIONS.find((option) => option.value === type) ?? undefined
	)
}

export function getProcessingTimeInHours(processing_time_in_hours?: string) {
	if (!processing_time_in_hours) return undefined
	const totalHours = Number(processing_time_in_hours)
	return {
		days: Math.floor(totalHours / 24),
		hours: totalHours % 24,
	}
}

export function visaProcessingTimeStr(
	processing_time_in_hours?: string,
	options: { compact?: boolean } = {}
): string | undefined {
	if (!processing_time_in_hours) return undefined
	const totalHours = Number(processing_time_in_hours)
	if (Number.isNaN(totalHours) || totalHours < 0) return undefined

	const days = Math.floor(totalHours / 24)
	const hours = totalHours % 24
	const { compact } = options

	// Both zero
	if (days === 0 && hours === 0) {
		return compact ? "0H" : `0 ${pluralize("Hour", hours)}`
	}

	// Only hours
	if (days === 0 && hours > 0) {
		return compact ? `${hours}H` : `${hours} ${pluralize("Hour", hours)}`
	}

	// Only days
	if (days > 0 && hours === 0) {
		return compact ? `${days}D` : `${days} ${pluralize("Day", days)}`
	}

	// Both days and hours
	if (days > 0 && hours > 0) {
		return compact
			? `${days}D, ${hours}H`
			: `${days} ${pluralize("Day", days)}, ${hours} ${pluralize("Hour", hours)}`
	}

	// fallback (should not hit, but just in case)
	return undefined
}

const visaTypeSchema = Validator.object().shape({
	name: Validator.string().required(
		"Please specify the name of the visa type."
	),
	type_of_visa: Validator.mixed().required("Please select the type of visa."),
	day_and_hour: Validator.mixed().required(
		"Please specify the processing time in days and hours."
	),
	duration: Validator.mixed().required("Please specify the duration."),
	entry_type: Validator.mixed().required("Please specify the type of entry."),
	purpose: Validator.mixed().required(
		"Please specify the purpose of the visa."
	),
	price_valid_till: Validator.string()
		.required("Please specify the date until which the current rate is valid.")
		.test(
			"is-day-after-today",
			"The rate validity date must be after today.",
			function (value) {
				if (!value) return false
				return isAfter(value, new Date(), "day")
			}
		),
	cost_price: Validator.number()
		.min(0, "The cost price per person must be zero or greater.")
		.required("Please enter the cost price per person."),
	selling_price: Validator.number()
		.min(0, "The selling price per person must be zero or greater.")
		.required("Please enter the selling price per person.")
		.test(
			"is-greater-than-cost-price",
			"Selling price must be greater than cost price to ensure a valid profit.",
			function (value) {
				const { cost_price } = this.parent
				if (typeof value === "number" && typeof cost_price === "number") {
					return value > cost_price
				}
				return true
			}
		),
	apply_before_days: Validator.string().nullable().optional(),
	document_groups: Validator.mixed().required(
		"Please select at least one required document."
	),
	description: Validator.string()
		.nullable()
		.optional()
		.max(
			1000,
			"Description can be up to 1000 characters. Please add any notes, instructions, or important details for this visa type here."
		),
})

const visaSchema = Validator.object().shape({
	countries: Validator.array()
		.min(1, "Please select at least one country.")
		.required("Please select at least one country."),
	destinations: Validator.array()
		.min(1, "Please select at least one destination.")
		.required("Please select at least one destination."),
	name: Validator.string().required("Please enter the visa name."),
	currency: Validator.mixed().required("Please select a currency."),
	document_groups: Validator.mixed().required(
		"Please select at least one required document."
	),
	visa_types: Validator.array()
		.min(1, "At least one visa type must be specified.")
		.required("At least one visa type is required.")
		.of(visaTypeSchema),
})

export const validateVisaForm = validateFormValues(visaSchema)
export const validateVisaTypeForm = validateFormValues(visaTypeSchema)

export function formatForWhatsApp(text: string) {
	let formatted = text

	// Headings -> Bold
	formatted = formatted.replace(/^###\s+(.+)$/gm, "*$1*\n")

	// Bold
	formatted = formatted.replace(/\*\*(.+?)\*\*/g, "*$1*")

	// Italic
	formatted = formatted.replace(/_(.+?)_/g, "_$1_")

	// Marked text -> Bold
	formatted = formatted.replace(/==(.+?)==/g, "*$1*")

	// Bullet points
	formatted = formatted.replace(/^-\s+(.+)$/gm, "• $1")
	formatted = formatted.replace(/^\s{4}-\s+(.+)$/gm, "    • $1")

	// Divider
	formatted = formatted.replace(/^---+$/gm, "────────────────")

	// Links
	formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1: $2")

	// Clean up multiple newlines
	formatted = formatted.replace(/\n{3,}/g, "\n\n")

	return formatted
}
